import React, { useState, useEffect } from 'react';
import { UserCircle, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/Button';
import { cn } from '../utils/cn';
import { supabase } from '../lib/supabase';
import { ReminderSettings } from '../components/ReminderSettings';
import { ProtectedLayout } from '../components/ProtectedLayout';
import { WeeklyCategoryManager } from '../components/WeeklyCategoryManager';

export function Profile() {
  const navigate = useNavigate();

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [coreValues, setCoreValues] = useState<string[]>([]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [showEmailChange, setShowEmailChange] = useState(false);
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [hasAiAccess, setHasAiAccess] = useState(false);
  const [timeZone, setTimeZone] = useState('');

  const timeZones = [
    'Pacific/Honolulu', 'America/Anchorage', 'America/Los_Angeles',
    'America/Denver', 'America/Chicago', 'America/New_York',
    'America/Phoenix', 'America/Toronto', 'Europe/London',
    'Europe/Berlin', 'Asia/Tokyo', 'Asia/Kolkata', 'Australia/Sydney'
  ];

  useEffect(() => {
    async function fetchProfile() {
      try {
        const { data: { session } } = await supabase.auth.getSession();

        if (!session?.user) {
          navigate('/');
          return;
        }

        const { data: profile, error: profileError } = await supabase
          .from('profiles')
          .select('name, email, core_value_1, core_value_2, core_value_3, time_zone')
          .eq('id', session.user.id)
          .single();

        if (profileError) throw profileError;

        if (profile) {
          setName(profile.name);
          setEmail(profile.email);
          setCoreValues([
            profile.core_value_1,
            profile.core_value_2,
            profile.core_value_3
          ].filter(Boolean));
          setTimeZone(profile.time_zone || Intl.DateTimeFormat().resolvedOptions().timeZone);
        }

        const { data: accessData } = await supabase
          .from("ai_test_access")
          .select("id")
          .eq("user_id", session.user.id)
          .single();

        if (accessData) {
          setHasAiAccess(true);
        }

      } catch (error: any) {
        console.error('Error fetching profile:', error);
        setError('Failed to load profile data');
      } finally {
        setIsLoading(false);
      }
    }

    fetchProfile();
  }, [navigate]);

  const handleEmailChange = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setIsSubmitting(true);

    try {
      if (!currentPassword) {
        throw new Error('Please enter your current password');
      }

      const { error: signInError } = await supabase.auth.signInWithPassword({
        email,
        password: currentPassword
      });

      if (signInError) throw new Error('Current password is incorrect');

      const { error: updateError } = await supabase.auth.updateUser({
        email: newEmail
      });

      if (updateError) throw updateError;

      const { error: profileError } = await supabase
        .from('profiles')
        .update({ email: newEmail })
        .eq('id', (await supabase.auth.getUser()).data.user?.id);

      if (profileError) throw profileError;

      setSuccess('Email update initiated. Please check your new email for verification.');
      setShowEmailChange(false);
      setCurrentPassword('');
      setNewEmail('');
    } catch (error: any) {
      console.error('Error updating email:', error);
      setError(error.message || 'Failed to update email');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setIsSubmitting(true);

    try {
      if (!currentPassword) {
        throw new Error('Please enter your current password');
      }

      if (newPassword.length < 6) {
        throw new Error('New password must be at least 6 characters long');
      }

      if (newPassword !== confirmPassword) {
        throw new Error('New passwords do not match');
      }

      const { error: signInError } = await supabase.auth.signInWithPassword({
        email,
        password: currentPassword
      });

      if (signInError) throw new Error('Current password is incorrect');

      const { error: updateError } = await supabase.auth.updateUser({
        password: newPassword
      });

      if (updateError) throw updateError;

      setSuccess('Password updated successfully');
      setShowPasswordChange(false);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error: any) {
      console.error('Error updating password:', error);
      setError(error.message || 'Failed to update password');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleProfileUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setIsSubmitting(true);

    try {
      const { data: { session } } = await supabase.auth.getSession();

      if (!session?.user) {
        throw new Error('Not authenticated');
      }

      const { error: profileError } = await supabase
        .from('profiles')
        .update({ 
          name: name.trim(),
          time_zone: timeZone
        })
        .eq('id', session.user.id);

      if (profileError) throw profileError;

      setSuccess('Profile updated successfully');
    } catch (error: any) {
      console.error('Error updating profile:', error);
      setError(error.message || 'Failed to update profile');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-white to-blue-50 flex items-center justify-center">
        <div className="text-[#236caf]">Loading...</div>
      </div>
    );
  }

  return (
    <ProtectedLayout>
    <div className="min-h-screen bg-gradient-to-b from-white to-blue-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-white rounded-xl shadow-lg p-8">
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-3">
              <UserCircle className="w-8 h-8 text-[#236caf]" />
              <h1 className="text-3xl font-bold text-[#004060]">Profile Settings</h1>
            </div>
            <Button
              variant="outline"
              onClick={() => navigate('/dashboard')}
              className="flex items-center gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Dashboard
            </Button>
          </div>

          {error && (
            <div className="mb-6 p-4 bg-red-50 text-red-700 rounded-lg">
              {error}
            </div>
          )}

          {success && (
            <div className="mb-6 p-4 bg-green-50 text-green-700 rounded-lg">
              {success}
            </div>
          )}

          {/* Name Change */}
          <form onSubmit={handleProfileUpdate} className="space-y-6 mt-6">
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                Name
              </label>
              <input
                type="text"
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className={cn(
                  "w-full px-4 py-2 rounded-lg border border-gray-300",
                  "focus:ring-2 focus:ring-[#236caf] focus:border-transparent"
                )}
                required
              />
            </div>
            
            <div>
              <label htmlFor="timezone" className="block text-sm font-medium text-gray-700 mb-2">
                Time Zone
              </label>
              <select
                id="timezone"
                value={timeZone}
                onChange={(e) => setTimeZone(e.target.value)}
                className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#236caf] focus:border-transparent"
              >
                {timeZones.map(tz => (
                  <option key={tz} value={tz}>{tz}</option>
                ))}
              </select>
              <p className="mt-1 text-sm text-gray-500">
                This will be used for all date and time displays in the app.
              </p>
            </div>
            
            <Button
              type="submit"
              variant="outline"
              disabled={isSubmitting}
              className="w-full"
            >
              {isSubmitting ? 'Updating...' : 'Update Profile'}
            </Button>
          </form>

          {/* Email Change */}
          <div className="border-t pt-8 mt-8">
            <h3 className="text-lg font-semibold text-gray-700 mb-4">Email Settings</h3>
            <p className="text-gray-600 mb-4">Current email: {email}</p>
            {!showEmailChange ? (
              <Button variant="outline" onClick={() => setShowEmailChange(true)} className="w-full">
                Change Email
              </Button>
            ) : (
              <form onSubmit={handleEmailChange} className="space-y-4">
                <div>
                  <label htmlFor="newEmail" className="block text-sm font-medium text-gray-700 mb-2">
                    New Email
                  </label>
                  <input
                    type="email"
                    id="newEmail"
                    value={newEmail}
                    onChange={(e) => setNewEmail(e.target.value)}
                    className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#236caf] focus:border-transparent"
                    required
                  />
                </div>
                <div>
                  <label htmlFor="currentPasswordEmail" className="block text-sm font-medium text-gray-700 mb-2">
                    Current Password
                  </label>
                  <input
                    type="password"
                    id="currentPasswordEmail"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#236caf] focus:border-transparent"
                    required
                  />
                </div>
                <div className="flex gap-2">
                  <Button type="submit" disabled={isSubmitting} className="flex-1">
                    {isSubmitting ? 'Updating...' : 'Update Email'}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setShowEmailChange(false);
                      setNewEmail('');
                      setCurrentPassword('');
                    }}
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            )}
          </div>

          {/* Password Change */}
          <div className="border-t pt-8 mt-8">
            <h3 className="text-lg font-semibold text-gray-700 mb-4">Password Settings</h3>
            {!showPasswordChange ? (
              <Button variant="outline" onClick={() => setShowPasswordChange(true)} className="w-full">
                Change Password
              </Button>
            ) : (
              <form onSubmit={handlePasswordChange} className="space-y-4">
                <div>
                  <label htmlFor="currentPassword" className="block text-sm font-medium text-gray-700 mb-2">
                    Current Password
                  </label>
                  <input
                    type="password"
                    id="currentPassword"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#236caf] focus:border-transparent"
                    required
                  />
                </div>
                <div>
                  <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700 mb-2">
                    New Password
                  </label>
                  <input
                    type="password"
                    id="newPassword"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#236caf] focus:border-transparent"
                    required
                    minLength={6}
                  />
                </div>
                <div>
                  <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-2">
                    Confirm New Password
                  </label>
                  <input
                    type="password"
                    id="confirmPassword"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#236caf] focus:border-transparent"
                    required
                    minLength={6}
                  />
                </div>
                <div className="flex gap-2">
                  <Button type="submit" disabled={isSubmitting} className="flex-1">
                    {isSubmitting ? 'Updating...' : 'Update Password'}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setShowPasswordChange(false);
                      setCurrentPassword('');
                      setNewPassword('');
                      setConfirmPassword('');
                    }}
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            )}
          </div>

          {/* Core Values */}
          <div className="border-t pt-8 mt-8">
            <h3 className="text-lg font-semibold text-gray-700 mb-4">Core Values</h3>
            <div className="space-y-2 mb-4">
              {coreValues?.map((value, index) => (
                <div
                  key={value}
                  className={cn(
                    "p-2 rounded-lg",
                    index === 0 && "bg-blue-50",
                    index === 1 && "bg-green-50",
                    index === 2 && "bg-purple-50"
                  )}
                >
                  <p className="font-medium text-gray-800">{value}</p>
                </div>
              ))}
            </div>
            <Button
              variant="outline"
              onClick={() => navigate('/update-core-values')}
              className="w-full"
            >
              Update Core Values
            </Button>
          </div>

{/* Subscription Area */}
<div className="border-t pt-8 mt-8">
  <h3 className="text-lg font-semibold text-gray-700 mb-4">Subscription</h3>
  <Button
    variant="outline"
    asChild
    className="w-full"
  >
    <a
      href="https://billing.stripe.com/p/login/test_3cIaEQ3MS9v69M660V2sM00"
      target="_blank"
      rel="noopener noreferrer"
    >
      Manage Billing in Stripe
    </a>
  </Button>
</div>

          {/* Weekly Category Manager */}
            {hasAiAccess && (
              <div className="border-t pt-8 mt-8">
                <h3 className="text-lg font-semibold text-gray-700 mb-4">Weekly Categories</h3>
                <WeeklyCategoryManager />
              </div>
            )}

            {/* Reminder Settings */}
            <div className="border-t pt-8 mt-8 bg-gradient-to-r from-[#004060] to-[#236caf] -mx-8 -mb-8 p-8 rounded-b-xl">
              <h3 className="text-lg font-semibold text-white mb-4">Reminder Settings</h3>
              <ReminderSettings />
            </div>
          </div>
        </div>
      </div>
    </ProtectedLayout>
  );
}